import { graphql } from 'react-apollo'
import { withRouter } from 'react-router'

import { ModalActionTypes } from '@acorns/web-components'
import { withFormik } from 'formik'
import { filter, isEmpty, omit, pathOr } from 'ramda'
import {
  branch,
  compose,
  fromRenderProps,
  lifecycle,
  renderComponent,
  withHandlers,
  withProps,
  withState,
} from 'recompose'

import { USER_TYPE } from 'app-constants'
import CenteredSpinner from 'components/CenteredSpinner'
import withAgreements from 'components/HOCs/WithAgreements'
import withHeroText from 'components/HOCs/withHeroText'
import withLiveContent from 'components/HOCs/WithLiveContent'
import withPartnerProps from 'components/HOCs/WithPartnerProps'
import withReferralProp from 'components/HOCs/WithReferralProp'
import withZestyOverrides from 'components/HOCs/WithZestyOverrides'
import getDefaultEventData from 'components/Trackers/defaults'
import { TRACKER_IDS } from 'components/Trackers/trackerIDs'
import { Funnel } from 'generated/types'
import { getButtonTappedEvent } from 'utils/analytics'
import trackEvent from 'utils/analytics/trackEvent'
import { withApolloErrorModal } from 'utils/Apollo'
import { withEnv } from 'utils/environment'
import { withFlowRouter } from 'utils/FlowProvider'
import { FlowRoute } from 'utils/FlowProvider/types'
import { getFlow } from 'utils/FlowProvider/utils'
import { FunnelContext, FunnelContextProps } from 'utils/FunnelContext'
import { withOptimizely } from 'utils/Optimizely'
import { withPrompt } from 'utils/prompts'
import removeDuplicateQueryParams from 'utils/sourceAttributes'
import withTieredRegistration from 'utils/TieredRegistration'

import {
  disclosureRow,
  emailAndPasswordRow,
  handleFormSubmit,
  mapPropsToValues,
  shouldRedirectToSuccessLander,
} from './form'
import createUser from './gql/createUser.mutation.gql'
import updatePersonalInfoMutation from './gql/updatePersonalInfo.mutation.gql'
import updateRoundUpProfile from './gql/updateRoundUpProfile.mutation.gql'
import { Props } from './types'
import { validate, validationErrorMap, validationSchema } from './validation'

export const enhance = compose<Props, {}>(
  fromRenderProps<Partial<FunnelContextProps>, {}, Partial<FunnelContextProps>>(
    FunnelContext.Consumer,
    ({ isDynamicFunnelEnabled, routeToPage }) => ({ isDynamicFunnelEnabled, routeToPage }),
  ),
  withEnv,
  withFlowRouter,
  withApolloErrorModal,
  withHeroText,
  withPartnerProps,
  withAgreements,
  withReferralProp,
  withPrompt,
  withRouter,
  withLiveContent,
  withZestyOverrides,
  withTieredRegistration,
  withOptimizely,
  graphql(createUser, { name: 'createUser' }),
  graphql(updatePersonalInfoMutation, {
    name: 'updatePersonalInfo',
  }),
  graphql(updateRoundUpProfile, {
    name: 'updateRoundUpProfile',
  }),
  withState('isEmailValid', 'setIsEmailValid', true),
  withState('submitEmailCount', 'setSubmitEmailCount', 0),
  withHandlers({
    getSourceAttributes: props => () => {
      const { partner, location, isProspectOnDirectToMobile } = props
      // Determine the source of this customer's arrival to Acorns
      let attributes = removeDuplicateQueryParams(window.location.search, true)
      let storedAttributes = removeDuplicateQueryParams(location?.state?.params)
      // We want to standardize passing in s1 attribute - if s1 is not provided, default to 'Organic'
      let id = pathOr(attributes.s1, ['org', 'name'], partner)

      const userType = attributes.usertype
      if (userType) {
        window.sessionStorage.setItem(USER_TYPE, userType)
      }

      let flow = getFlow()
      if (flow === 'tiered') {
        flow = 'tiered_reg'
      } else if (flow === 'directToMobile') {
        flow = 'direct_to_mobile'
      }

      // Note: If the `clickid` query param exists set the source.id as `impactradius`
      // Note: This should really be done by the backend.
      /* istanbul ignore else */
      if (attributes.clickid) {
        id = 'impactradius'
      }

      // Save all attributes before omitting some
      // The below will be removed once we move source attributes to registration service (rather than backend)
      const allAttributes = { ...attributes, ...storedAttributes, flow }

      // Note We do this because source_id = s1
      attributes = omit(['s1', ...(id === 'Referrals' ? ['referral_code'] : [])], attributes)
      storedAttributes = omit(['tierPriceId', 'referral_code'], storedAttributes)

      attributes = { ...attributes, flow, ...storedAttributes }

      return {
        id,
        attributes: JSON.stringify(attributes),
        raw: {
          // should only be used for registration service updates
          ...allAttributes,
          s1: id ?? 'Organic',
          ...(isProspectOnDirectToMobile && { flow: Funnel.DirectToMobile }),
        },
      }
    },
    updateUserProfile: ({ partner, updatePersonalInfo }) => (email: string) => {
      // Update user profile
      const partnerUserData = filter(key => key !== null && key !== '', partner.user)

      // Always use the email address entered by the user, not the one provided
      // by the partner (if provided)
      partnerUserData.email = email

      return new Promise<void>(resolve => {
        // Don't bother with updating the user profile if none was provided by the partner
        /* istanbul ignore else */
        if (isEmpty(partnerUserData)) {
          resolve()
        } else {
          // Update user profile data
          updatePersonalInfo({ variables: partnerUserData }).then(() => {
            resolve()
          })
        }
      })
    },
  }),
  withFormik({
    enableReinitialize: true,
    isInitialValid: true,
    mapPropsToValues,
    handleSubmit: handleFormSubmit,
    validate,
    validateOnChange: false,
    validationSchema,
  }),
  withProps(props => {
    const { heroText, liveContent, isEarlyTier, isTieredRegEnabled, tierPrice } = props

    const attributes = removeDuplicateQueryParams(location.search)
    return {
      emailAndPasswordRow,
      disclosureRow: disclosureRow(props.openAgreementModal, props.partner),
      pageNotFound: pathOr(false, ['location', 'state', 'pageNotFound'], props.history),
      header: heroText.header,
      text: heroText.text,
      isMilitary: attributes.s1 === 'military',
      isMOCAttribution: attributes.s3 === 'MightyOakCard',
      isTieredRegEnabled,
      footnote: !isEarlyTier && liveContent('hero_footnote'),
      footnoteLock: !isEarlyTier && liveContent('hero_footnote_lock', { renderer: 'raw' }),
      submitCta: liveContent('submit_cta', { fallback: 'Create account' }),
      tierPrice,
      isProspectOnDirectToMobile: shouldRedirectToSuccessLander(props),
    }
  }),
  lifecycle<Props, any>({
    componentDidMount() {
      this.props.loadDataWithGQL()
    },
  }),
  withHandlers<Props, Partial<Props>>({
    setFieldValue: ({ setFieldValue }) => (field, value) => {
      setFieldValue(field, value, true)
    },
    onSubmit: props => () => {
      const { promptUser, submitCount, submitForm, values } = props

      // Attempt submit
      submitForm()

      // If the number of attempts exceeds 2, display a dialog
      if (submitCount > 2) {
        validationSchema(props)
          .validate(values)
          .catch(error => {
            const message = validationErrorMap.get(error.path)
            promptUser({
              title: 'Missed a step',
              message,
              actions: [
                {
                  type: ModalActionTypes.primary,
                  label: 'Okay',
                },
              ],
            })
          })
      }
    },
    onEmailChange: props => async e => {
      // https://github.com/jaredpalmer/formik/issues/1218#issuecomment-451119016
      await props.handleChange(e)

      if (props.submitEmailCount > 0) {
        const errors = await props.validateForm()
        props.setIsEmailValid(!errors.email)
      }
    },
    onChange: props => async e => {
      // https://github.com/jaredpalmer/formik/issues/1218#issuecomment-451119016
      await props.handleChange(e)

      if (e.target.id === 'usResident') {
        trackEvent('Button Tapped', {
          eventData: {
            ...getButtonTappedEvent(TRACKER_IDS.SIGNUP.US_RESIDENT_AGREEMENT_BUTTON),
            ...getDefaultEventData('Button Tapped'),
          },
        })
      }

      // Only validate the form if the user has attempted at least one submit
      if (props.submitCount > 0) {
        props.validateForm()
      }
    },
    handleCompareTiersRedirect: ({ route, location }) => () => {
      route({
        from: FlowRoute.Signup,
        action: 'select-tier',
        locationState: { params: location?.state?.params || window.location.search || '' },
        forwardQueryParams: true,
      })
    },
  }),
  branch(({ isPartnerReg, partner }) => isPartnerReg && !partner, renderComponent(CenteredSpinner)),
)
